import { toast } from "react-toastify";

import { store } from "store/store";

import { messagesApi } from "components/services/messages/messagesService";

/**
 * Unified toast wrapper.
 *
 *   notify.success("UI_FILE_UPLOAD_SUCCESS");
 *   notify.error("UI_FILE_UPLOAD_FAILED", { error: err.message });
 *   notify.apiError(err, "UI_FILE_UPLOAD_FAILED", { error: err.message });
 *
 * - Looks up the key in the cached message catalog (loaded by App.jsx via
 *   messagesApi.getUiMessages).
 * - Substitutes `{var}` placeholders from the `vars` object.
 * - If the key is unknown (catalog not loaded yet, or typo) falls back to
 *   displaying the key itself so the user still sees something and the
 *   developer notices in QA.
 * - Applies consistent visual options per severity (theme, autoClose).
 */

const TOAST_OPTIONS = {
  success: { theme: "colored", autoClose: 4000 },
  error: { theme: "colored", autoClose: 6000 },
  warning: { theme: "colored", autoClose: 5000 },
  info: { theme: "colored", autoClose: 4000 },
};

function getCatalog() {
  const selectResult = messagesApi.endpoints.getUiMessages.select()(
    store.getState()
  );
  return selectResult?.data ?? {};
}

export function resolveMessage(key, vars = {}) {
  const catalog = getCatalog();
  let text = catalog[key] ?? key;
  for (const [name, value] of Object.entries(vars)) {
    text = text.replaceAll(`{${name}}`, String(value));
  }
  return text;
}

function extractApiError(err) {
  if (!err) return null;
  if (typeof err === "string") return err;
  return err?.data?.detail ?? err?.message ?? null;
}

export const notify = {
  success(key, vars) {
    toast.success(resolveMessage(key, vars), TOAST_OPTIONS.success);
  },
  error(key, vars) {
    toast.error(resolveMessage(key, vars), TOAST_OPTIONS.error);
  },
  warning(key, vars) {
    toast.warning(resolveMessage(key, vars), TOAST_OPTIONS.warning);
  },
  info(key, vars) {
    toast.info(resolveMessage(key, vars), TOAST_OPTIONS.info);
  },
  /**
   * Show an error from an API call. Prefers backend-provided detail; falls
   * back to the supplied UI key if backend didn't return one.
   */
  apiError(err, fallbackKey, vars) {
    const backendText = extractApiError(err);
    const text = backendText ?? resolveMessage(fallbackKey, vars);
    toast.error(text, TOAST_OPTIONS.error);
  },
};
